import { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { Clock, AlertTriangle, ExternalLink, Utensils } from "lucide-react";
import { business } from "../data/business";

// Compute the current weekday in Eastern Time. The storefront publishes ET
// hours; if a visitor in Pacific time loads the page Sunday 9pm PT it is
// already Monday in Benton — using the local clock would highlight the
// wrong row.
function etWeekday(): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "America/New_York",
  });
}

// Format hh:mm AM/PM in a given timezone (or local if undefined).
function fmtTime(tz?: string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(new Date());
}

// Detect the visitor's current IANA timezone via Intl. Falls back to
// undefined when the browser refuses to tell us, in which case we just
// don't render the dual-clock (better to hide than to fake it).
function detectLocalTz(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function Hours() {
  const root = useRef<HTMLElement>(null);
  const [now, setNow] = useState<Date>(() => new Date());
  const localTz = detectLocalTz();
  // Don't render the dual clock if the visitor is already on ET — same
  // number twice is just noise. We compare offsets at "now" because IANA
  // names ("America/New_York" vs "US/Eastern") can differ but resolve to
  // the same wall clock.
  const isInET = (() => {
    if (!localTz) return false;
    if (localTz === "America/New_York") return true;
    try {
      const local = fmtTime(localTz);
      const et = fmtTime("America/New_York");
      return local === et;
    } catch {
      return false;
    }
  })();

  // Re-render every minute so the clock stays current (#71).
  useEffect(() => {
    if (isInET) return; // no clock rendered → no need to tick
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [isInET]);

  // Touch `now` so eslint doesn't complain — fmtTime uses `new Date()` directly
  // but we still want the dependency to drive re-render.
  void now;

  useGSAP(
    () => {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      gsap.from(".hours-row", {
        x: -20,
        opacity: 0,
        duration: 0.5,
        stagger: 0.06,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 80%" },
      });
      gsap.from(".hours-intro > *", {
        y: 18,
        opacity: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 80%" },
      });
    },
    { scope: root },
  );

  const todayName = etWeekday();

  return (
    <section ref={root} id="hours" className="hours-wrap" aria-label="Operating hours">
      <div className="hours-intro">
        <div className="eye-row">
          <Clock aria-hidden="true" />
          <span className="label">HOURS · EASTERN TIME (ET)</span>
        </div>
        <h2 className="h2">Plan your visit before you drive in</h2>

        {/* #71 — live dual-TZ clock. Hidden when the visitor is already on ET. */}
        {!isInET && (
          <p className="hours-clock" aria-live="polite">
            <span>
              Your time: <strong>{fmtTime(localTz)}</strong>
            </span>
            <span className="hours-clock-sep" aria-hidden="true">·</span>
            <span>
              Ocoee (ET): <strong>{fmtTime("America/New_York")}</strong>
            </span>
          </p>
        )}

        {/* item 50: bright closed-days callout */}
        <p className="hours-closed-banner" role="alert">
          <AlertTriangle aria-hidden="true" />
          <span>
            <strong>Closed Tuesday & Wednesday.</strong> Confirm hours
            before you drive out.
          </span>
        </p>
        <p className="body">
          The river flows on scheduled dam-release days, and the storefront
          keeps tighter midweek hours. Call ahead if you&rsquo;re coming from
          out of town.
        </p>

        <div className="hours-note">
          <AlertTriangle aria-hidden="true" />
          <span>{business.seasonNote}</span>
        </div>

        <div className="hours-note hours-note-info">
          <Utensils aria-hidden="true" />
          <span>
            <strong>Restaurants run separate hours.</strong> Sugarloaf Bar &
            Grill and Blue Hole BBQ open seasonally; call before driving over
            in spring and fall.
          </span>
        </div>

        {/* URL goes to the TVA's lake-level / release page for Ocoee Dam #1.
            Re-verify before launch — TVA reorganises their site occasionally. */}
        <a
          href="https://www.tva.com/Environment/Lake-Levels/Ocoee-1"
          target="_blank"
          rel="noopener noreferrer"
          className="hours-tva-link"
        >
          <ExternalLink aria-hidden="true" /> View the Ocoee dam-release schedule
        </a>
      </div>

      <div className="hours-card">
        {business.hours.map((h) => {
          const isToday = h.day === todayName;
          return (
            <div
              key={h.day}
              className={`hours-row ${h.closed ? "is-closed" : "is-open"} ${
                isToday ? "is-today" : ""
              }`}
            >
              <span className="day">
                {h.day}
                {/* New round #61 */}
                {isToday && <em className="today-tag"> · Today</em>}
              </span>
              <span className="time">
                {h.closed ? "—" : `${h.open} – ${h.close}`}
              </span>
              <span className="badge">
                {h.closed ? "Closed" : "Open"}
              </span>
            </div>
          );
        })}
        {/* New round #62 */}
        <NextOpenLine />
        {/* #74 — "Add to calendar" intentionally NOT rendered. These rows
            are a recurring weekly storefront schedule, not one-off events.
            A .ics for "every Saturday 9–7 forever" pollutes calendars and
            isn't useful. Revisit if/when the page renders dated events
            (e.g. a release weekend, a guided night paddle). */}
        <p className="hours-fineprint">
          Hours may vary by season &amp; on major holidays · all times Eastern (ET).
        </p>
      </div>
    </section>
  );
}

// New round #62: surface "Opens [Day] [Time]" if today is closed, otherwise "Closes at …"
function NextOpenLine() {
  const todayName = etWeekday();
  const todayRow = business.hours.find((h) => h.day === todayName);

  // If today is open, tell them what time we close
  if (todayRow && !todayRow.closed) {
    return (
      <p className="hours-next is-open">
        Open today · closes at <strong>{todayRow.close}</strong>
      </p>
    );
  }

  // Otherwise walk forward to the next open day
  const orderedDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const todayIdx = orderedDays.indexOf(todayName);
  for (let i = 1; i <= 7; i += 1) {
    const dayName = orderedDays[(todayIdx + i) % 7];
    const row = business.hours.find((h) => h.day === dayName);
    if (row && !row.closed) {
      return (
        <p className="hours-next is-closed">
          Closed today · opens <strong>{row.day} at {row.open}</strong>
        </p>
      );
    }
  }
  return null;
}
